import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Phone, MessageCircle, Calendar, Gauge, Palette, Fuel, Cog } from "lucide-react";
import { apiClient, fileUrl, formatPrice, PHONE_TEL, PHONE_NUMBER } from "../lib/api";
import Seo from "../components/Seo";

const FALLBACK = "https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=1600&auto=format&fit=crop";

const CarDetail = () => {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    apiClient
      .get(`/cars/${id}`)
      .then((r) => setCar(r.data))
      .catch(() => setCar(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="bg-zinc-950 text-white pt-32 pb-24 min-h-screen">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="aspect-video bg-zinc-900 animate-pulse mb-6" />
          <div className="h-12 w-2/3 bg-zinc-900 animate-pulse" />
        </div>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="bg-zinc-950 text-white pt-32 pb-24 min-h-screen">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <h1 className="font-display text-4xl uppercase mb-6">Araç bulunamadı</h1>
          <Link to="/ikinci-el-araclar" className="text-orange-500 uppercase tracking-widest text-sm">
            Araçlara Dön
          </Link>
        </div>
      </div>
    );
  }

  const images = car.images && car.images.length > 0 ? car.images.map(fileUrl) : [FALLBACK];
  const whatsappMsg = encodeURIComponent(
    `Merhaba, ${car.year} ${car.brand} ${car.model} aracı hakkında bilgi almak istiyorum.`
  );

  const productSchema = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: `${car.year} ${car.brand} ${car.model}`,
    image: images,
    description:
      car.description ||
      `${car.year} ${car.brand} ${car.model} ikinci el araç Gensan Motors İstanbul'da satışta.`,
    brand: { "@type": "Brand", name: car.brand },
    sku: car.id,
    category: "Otomobil",
    productionDate: String(car.year),
    itemCondition: "https://schema.org/UsedCondition",
    additionalProperty: [
      { "@type": "PropertyValue", name: "Yıl", value: String(car.year) },
      ...(car.mileage != null ? [{ "@type": "PropertyValue", name: "Kilometre", value: `${car.mileage} km` }] : []),
      ...(car.fuel_type ? [{ "@type": "PropertyValue", name: "Yakıt Tipi", value: car.fuel_type }] : []),
      ...(car.transmission ? [{ "@type": "PropertyValue", name: "Vites", value: car.transmission }] : []),
      ...(car.color ? [{ "@type": "PropertyValue", name: "Renk", value: car.color }] : []),
    ],
    offers: {
      "@type": "Offer",
      priceCurrency: "TRY",
      price: String(car.price),
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/UsedCondition",
      url: `https://gensanmotors.com.tr/araba/${car.id}`,
      seller: { "@type": "Organization", name: "Gensan Motors" },
    },
  };

  return (
    <div data-testid="car-detail-page" className="bg-zinc-950 text-white pt-28 pb-24 min-h-screen">
      <Seo
        title={`${car.year} ${car.brand} ${car.model}`}
        description={`${car.year} ${car.brand} ${car.model} ikinci el araç ${formatPrice(car.price)} fiyatla Gensan Motors İstanbul'da satışta. ${car.description ? car.description.slice(0, 120) : "Detaylar ve iletişim için tıklayın."}`}
        keywords={`${car.brand} ${car.model}, ${car.brand} fiyat, ikinci el ${car.brand}`}
        path={`/araba/${car.id}`}
        image={images[0]}
        jsonLd={productSchema}
      />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <Link to="/ikinci-el-araclar" data-testid="back-to-cars" className="inline-flex items-center gap-2 text-zinc-400 hover:text-orange-500 uppercase tracking-widest text-xs mb-8 transition-colors">
          <ArrowLeft size={16} /> Geri Dön
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <div className="aspect-[4/3] overflow-hidden bg-zinc-900 mb-4">
              <img
                src={images[activeIdx]}
                alt={`${car.brand} ${car.model}`}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = FALLBACK; }}
              />
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveIdx(i)}
                    data-testid={`car-thumb-${i}`}
                    className={`aspect-square overflow-hidden border-2 ${
                      activeIdx === i ? "border-orange-500" : "border-transparent hover:border-zinc-700"
                    } transition-colors`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest bg-white text-black">
                2. El Araç
              </span>
              {car.is_featured && (
                <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest bg-black border border-orange-600 text-orange-500">
                  Öne Çıkan
                </span>
              )}
            </div>

            <p className="text-orange-500 font-bold uppercase tracking-widest text-sm mb-2">
              {car.brand}
            </p>
            <h1 data-testid="car-title" className="font-display text-4xl md:text-5xl font-bold uppercase leading-none tracking-tight mb-6">
              {car.model}
            </h1>
            <div className="text-5xl font-display font-bold text-white mb-8">
              {formatPrice(car.price)}
            </div>

            <div className="grid grid-cols-2 gap-px bg-zinc-900 border border-zinc-900 mb-8">
              <div className="bg-zinc-950 p-5">
                <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest mb-2">
                  <Calendar size={14} /> Yıl
                </div>
                <div className="text-xl font-bold">{car.year}</div>
              </div>
              {car.mileage != null && (
                <div className="bg-zinc-950 p-5">
                  <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest mb-2">
                    <Gauge size={14} /> KM
                  </div>
                  <div className="text-xl font-bold">{car.mileage.toLocaleString("tr-TR")}</div>
                </div>
              )}
              {car.fuel_type && (
                <div className="bg-zinc-950 p-5">
                  <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest mb-2">
                    <Fuel size={14} /> Yakıt
                  </div>
                  <div className="text-xl font-bold">{car.fuel_type}</div>
                </div>
              )}
              {car.transmission && (
                <div className="bg-zinc-950 p-5">
                  <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest mb-2">
                    <Cog size={14} /> Vites
                  </div>
                  <div className="text-xl font-bold">{car.transmission}</div>
                </div>
              )}
              {car.color && (
                <div className="bg-zinc-950 p-5">
                  <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest mb-2">
                    <Palette size={14} /> Renk
                  </div>
                  <div className="text-xl font-bold">{car.color}</div>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 mb-8">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="car-call-button"
                className="inline-flex items-center justify-center gap-3 bg-orange-600 hover:bg-orange-700 text-white px-6 py-4 font-bold uppercase tracking-widest text-sm transition-colors"
              >
                <Phone size={18} /> Hemen Ara: {PHONE_NUMBER}
              </a>
              <a
                href={`https://wa.me/${PHONE_TEL.replace(/[^0-9]/g, '')}?text=${whatsappMsg}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="car-whatsapp-button"
                className="inline-flex items-center justify-center gap-3 border border-white/40 hover:border-white hover:bg-white hover:text-black text-white px-6 py-4 font-bold uppercase tracking-widest text-sm transition-all"
              >
                <MessageCircle size={18} /> WhatsApp ile İletişim
              </a>
            </div>

            {car.description && (
              <div className="border-t border-zinc-900 pt-6">
                <h3 className="font-impact text-sm uppercase tracking-widest text-orange-500 mb-3">Açıklama</h3>
                <p className="text-zinc-300 leading-relaxed whitespace-pre-line">{car.description}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarDetail;
